import { memo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { IPreset } from "../services/presets/presetService";
import { Gift, DollarSign } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/ui/tooltip";

interface PresetCardProps {
  preset: IPreset;
  cover: string;
  published: boolean;
  name: string;
  creator?: {
    name: string;
    avatar: string;
  };
  packName?: string;
  isFree?: boolean;
}

const statusStyles = {
  published: {
    bg: "bg-[#8FD460]/10",
    text: "text-[#8FD460]",
    label: "Publicado",
  },
  not_published: {
    bg: "bg-[#91CAFF]/10",
    text: "text-[#91CAFF]",
    label: "Não Publicado",
  },
};

export const PresetCard = memo(({
  preset,
  cover,
  published,
  name,
  creator,
  packName,
  isFree = false,
}: PresetCardProps) => {
  const navigate = useNavigate();
  const status = published ? "published" : "not_published";
  const style = statusStyles[status];

  const handleClick = useCallback(() => {
    // Navegar direto para a edição do preset
    const packId = preset.packId || preset.pack?.id;
    if (preset.id && packId) {
      navigate(`/presets/${preset.id}`, {
        state: {
          packId: packId,
          packName: packName,
        },
      });
    }
  }, [navigate, preset, packName]);

  return (
    <TooltipProvider>
      <article
        onClick={handleClick}
        className="group flex flex-col overflow-hidden rounded-lg border border-white/[0.06] hover:border-white/10 transition-colors cursor-pointer"
      >
        <div className="relative aspect-[4/5] w-full overflow-hidden">
          <img
            src={cover}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            alt={name}
          />
          {/* Badge flutuante para tipo (Grátis/Pago) */}
          <div className="absolute top-2 right-2">
            <Tooltip>
              <TooltipTrigger asChild>
                {isFree ? (
                  <div className="flex items-center justify-center h-7 w-7 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 cursor-pointer">
                    <Gift className="h-3.5 w-3.5 text-white/70" />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-7 w-7 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 cursor-pointer">
                    <DollarSign className="h-3.5 w-3.5 text-white/70" />
                  </div>
                )}
              </TooltipTrigger>
              <TooltipContent>
                <p>{isFree ? "Grátis" : "Pago"}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      <div className="flex flex-col gap-3 p-4">
        <span
          className={`inline-flex h-6 items-center justify-center rounded px-2 text-xs font-medium ${style.bg} ${style.text}`}
        >
          {style.label}
        </span>
        <div className="text-white">
          <h3 className="font-medium">{name}</h3>
          
          {/* Creator com foto e nome */}
          {creator ? (
            <div className="mt-2 flex items-center gap-2">
              <img
                src={creator.avatar || "/placeholder.svg"}
                alt={creator.name}
                className="h-6 w-6 rounded-full object-cover"
              />
              <p className="text-sm text-white/60">{creator.name}</p>
            </div>
          ) : null}

          {/* Linha do pack */}
          {packName && (
            <p className="mt-1 text-xs text-white/40">{packName}</p>
          )}
        </div>
      </div>
    </article>
    </TooltipProvider>
  );
});

PresetCard.displayName = "PresetCard";

